<template>
	<div class="container pb-10">
		<div class="flex flex-col items-start sm:flex-row sm:justify-between sm:items-center gap-2 py-2 mb-4">
			<h1 class="page-title">Configurator nou</h1>
		</div>

		<div class="light-grey rounded mb-4 p-4">
			<div class="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-2 lg:gap-10 w-full">
				<div class="w-full lg:w-1/2 flex flex-row items-center gap-2">
					<v-text-field v-model="filterInput" clearable flat solo hide-details
						prepend-inner-icon="mdi-magnify" label="Cauta configurație dupa client"></v-text-field>
				</div>
				<div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full lg:w-auto">
					<v-select ref="selectCountry" v-model="selectedCountry" class="w-full sm:w-52" :items="countryList"
						item-text="denumire" item-value="value" clearable @click:clear="$refs.selectCountry.blur()"
						label="Toate țările" solo hide-details>
						<template v-slot:item="{ item }">
							<span>
								{{ item.flag }} {{ item.denumire }}
							</span>
						</template>
						<template v-slot:selection="{ item }">
							<span>
								{{ item.flag }} {{ item.denumire }}
							</span>
						</template>
					</v-select>
					<div class="w-full sm:w-60">
						<v-menu v-model="monthpickerMenu" class="w-full" :close-on-content-click="false" left
							transition="scale-transition" offset-y min-width="auto">
							<template v-slot:activator="{ on, attrs }">
								<v-text-field v-model="monthText" class="date-field" label="Luna" solo
									prepend-inner-icon="mdi-calendar" hide-details readonly v-bind="attrs"
									v-on="on"></v-text-field>
							</template>
							<v-date-picker v-model="selectedMonth" no-title type="month"
								@input="changeMonth"></v-date-picker>
						</v-menu>
					</div>
				</div>
			</div>
		</div>

		<v-data-table :headers="headers" :items="filterBy(visibleConfigurations, filterInput, selectedCountry)"
			item-key="id" :loading="loadingConfigurations" :items-per-page="-1" hide-default-footer>
			<template v-slot:item.created_at="{ item }">
				{{ item.created_at | formatDate }}
			</template>
			<template v-slot:item.client="{ item }">
				{{ item.client_first_name }} {{ item.client_last_name }}
			</template>
			<template v-slot:item.origin_country="{ item }">
				<span v-if="item.origin_country" class="text-2xl">{{ displayFlag(item.origin_country) }}</span>
				<span v-else>-</span>
			</template>
			<template v-slot:item.is_kiosk="{ item }">
				<div v-if="item.is_kiosk" class="flex flex-row items-center justify-center gap-1">
					<v-icon color="teal">mdi-check</v-icon>
					<span>{{ item.origin_subdomain }}</span>
				</div>
				<span v-else>-</span>
			</template>
			<template v-slot:item.status="{ item }">
				{{ item.status | NoData }}
			</template>
			<template v-slot:item.pdf="{ item }">
				<v-btn icon text color="teal" @click="downloadPdf(item)">
					<v-icon>mdi-cloud-download</v-icon>
				</v-btn>
			</template>
		</v-data-table>
	</div>
</template>

<script>
import { mapState, mapActions } from "vuex"
import flags from "@/assets/countries-flags.json"
import { isExportOnly } from "@/utils"

export default {
	name: "OferteProductieConfiguratorNou",
	data() {
		return {
			filterInput: "",
			selectedCountry: null,
			monthpickerMenu: false,
			selectedMonth: new Date().toISOString().substring(0, 7),
			headers: [
				{
					text: 'Nr.',
					align: 'start',
					sortable: false,
					value: 'numeric_id',
					width: '5%'
				},
				{
					text: 'ID',
					align: 'start',
					sortable: false,
					value: 'id',
					width: '5%'
				},
				{
					text: 'Data',
					align: 'start',
					sortable: false,
					value: 'created_at',
					width: '10%'
				},
				{
					text: 'Client',
					align: 'start',
					sortable: false,
					value: 'client',
				},
				{
					text: 'Telefon',
					align: 'start',
					sortable: false,
					value: 'client_phone',
				},
				{
					text: 'Email',
					align: 'start',
					sortable: false,
					value: 'client_email',
				},
				{
					text: 'Regiune',
					align: 'start',
					sortable: false,
					value: 'client_region',
				},
				{
					text: 'Tara',
					align: 'center',
					sortable: false,
					value: 'origin_country',
					width: '8%'
				},
				{
					text: 'Showroom',
					align: 'center',
					sortable: false,
					value: 'is_kiosk',
					width: '8%'
				},
				{
					text: 'Status',
					align: 'center',
					sortable: false,
					value: 'status',
				},
				{
					text: 'PDF',
					align: 'center',
					sortable: false,
					value: 'pdf',
					width: '5%'
				},
			],
		};
	},
	computed: {
		...mapState('configuratorApp', ['configurations', 'loadingConfigurations']),
		// export agents see only configurations from outside Romania
		visibleConfigurations() {
			const user = this.$store.getters.loggedUser;
			return isExportOnly(user) ? this.configurations.filter((c) => c.origin_country !== "ro") : this.configurations;
		},
		monthText() {
			return this.$options.filters.formatMonth(this.selectedMonth)
		},
		firstDay() {
			return new Date(this.selectedMonth).toISOString().split('T')[0]
		},
		lastDay() {
			return new Date(new Date(this.selectedMonth).getFullYear(), new Date(this.selectedMonth).getMonth() + 1, 1).toISOString().split('T')[0]
		},
		countryList() {
			const countries = this.visibleConfigurations.reduce((acc, item) => {
				const country = item.origin_country;
				if (country && !acc.some(c => c.value == country)) {
					const countryDetails = flags[country.toUpperCase()]
					if (countryDetails) {
						acc.push({ value: country, denumire: countryDetails.name, flag: countryDetails.emoji });
					}
				}
				return acc.sort((a, b) => a.denumire.localeCompare(b.denumire));
			}, []);
			return countries;
		},
	},
	methods: {
		...mapActions('configuratorApp', ['fetchConfigurations', 'downloadConfigurationPdf']),
		async downloadPdf(item) {
			const data = await this.downloadConfigurationPdf(item.id);
			const blob = new Blob([data], { type: 'application/pdf' });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = `configurator-${item.id}.pdf`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		},
		escapeRegExp(s) {
			return s.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
		},
		displayFlag(country) {
			const flagCountry = flags[country.toUpperCase()]
			return flagCountry ? flagCountry.emoji : country;
		},
		filterBy(list, value, country) {
			if (!value) {
				value = ""
			}
			const words = value
				.split(/\s+/g)
				.map((s) => s.trim())
				.filter((s) => !!s);

			const hasTrailingSpace = value.endsWith(" ");

			const searchRegex = new RegExp(
				words
					.map((word, i) => {
						if (i + 1 === words.length && !hasTrailingSpace) {
							return `(?=.*\\b${this.escapeRegExp(word)})`;
						} else {
							return `(?=.*\\b${this.escapeRegExp(word)}\\b)`;
						}
					})
					.join("") + ".+",
				"i"
			);

			return list.filter((item) => {
				const fullName = `${item.client_first_name || ''} ${item.client_last_name || ''}`.trim();
				const email = item.client_email || '';
				const searchableText = `${fullName} ${email}`.trim();

				const matchesSearch = !value || searchRegex.test(searchableText);
				const matchesCountry = !country || item.origin_country === country;

				return matchesSearch && matchesCountry;
			});
		},
		changeMonth() {
			this.monthpickerMenu = false
			this.fetchConfiguratorConfigurations()
		},
		fetchConfiguratorConfigurations() {
			this.fetchConfigurations({ from: this.firstDay, to: this.lastDay })
		},
	},
	created: function () {
		this.fetchConfiguratorConfigurations()
	},
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
::v-deep .v-data-table__mobile-table-row {
	display: block !important;
	margin-bottom: 12px;
}
</style>
